import { useCallback } from 'react';
import { useSnackbar } from 'notistack';

import { getLocalizedErrorMessage } from '@features/assessment/utils/error-utils';
import { useTrans } from '@libs/i18n';

/**
 * Returns a mutation `onError` handler that surfaces a backend error as an error
 * toast. `getLocalizedErrorMessage` prefers the backend's localized `errorCode`,
 * then its raw `message`, then the given fallback key — so the toast always
 * reflects what the server actually said (e.g. a 422 validation reason or a 409
 * category lock).
 *
 * Lives in the shared hooks folder rather than inside a feature: tenant-profile
 * and remediation both need it, and a second copy would drift from the first.
 */
export function useBackendErrorToast(fallbackKey: string) {
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();
  return useCallback(
    (error: unknown) => {
      // `fallbackKey` is dynamic, so resolve it through a loosened `t`.
      const fallback = (t as (key: string) => string)(fallbackKey);
      enqueueSnackbar(getLocalizedErrorMessage(error, t, fallback), {
        variant: 'error',
      });
    },
    [enqueueSnackbar, t, fallbackKey],
  );
}
